import React from 'react';
import { Link } from 'react-router-dom';
import { rgbToHex, hexToRgb, rgbToHsl } from '../utils/colors';
import { generateTints, generateShades } from '../utils/colorAnalysis';
import type { RGB } from '../types';

interface TintsAndShadesProps {
  color: RGB;
  onColorSelect?: (hex: string) => void;
  selectedHex?: string | null;
}

const MIN_LIGHTNESS = 8;

const TintsAndShades: React.FC<TintsAndShadesProps> = ({ color, onColorSelect, selectedHex }) => {
  const hex = rgbToHex(color.r, color.g, color.b);
  const tints = generateTints(hex, 9);
  const shades = generateShades(hex, 9);

  const renderGrid = (colors: string[], isDark: boolean) => (
    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
      {colors.map((c) => {
        const rgb = hexToRgb(c);
        const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : { h: 0, s: 0, l: 0 };
        const isTooDark = hsl.l < MIN_LIGHTNESS;

        const overlay = (
          <div className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 ${isDark ? 'bg-white/10' : 'bg-black/10'} transition-opacity`}>
            <span className="text-xs font-mono text-white bg-black/50 px-1 rounded">{c}</span>
          </div>
        );

        if (isTooDark) {
          if (onColorSelect) {
            return (
              <button
                key={c}
                onClick={() => onColorSelect(c)}
                className={`group relative aspect-square rounded-lg overflow-hidden ring-1 opacity-60
                  ${selectedHex === c ? 'ring-2 ring-brand-500 scale-105' : 'ring-zinc-200/50 hover:ring-brand-500'}
                  transition-all`}
                style={{ backgroundColor: c }}
                title={c}
              >
                {overlay}
              </button>
            );
          }
          return (
            <div
              key={c}
              className="group relative aspect-square rounded-lg overflow-hidden ring-1 ring-zinc-200/50 opacity-60 cursor-default"
              style={{ backgroundColor: c }}
              title={c}
            >
              {overlay}
            </div>
          );
        }

        if (onColorSelect) {
          return (
            <button
              key={c}
              onClick={() => onColorSelect(c)}
              className={`group relative aspect-square rounded-lg overflow-hidden ring-1
                ${selectedHex === c ? 'ring-2 ring-brand-500 scale-105' : 'ring-zinc-200/50 hover:ring-brand-500'}
                transition-all`}
              style={{ backgroundColor: c }}
            >
              {overlay}
            </button>
          );
        }

        return (
          <Link
            key={c}
            to={`/color/${c.replace('#', '')}`}
            className="group relative aspect-square rounded-lg overflow-hidden ring-1 ring-zinc-200/50 hover:ring-brand-500 transition-all"
            style={{ backgroundColor: c }}
          >
            {overlay}
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="mb-6">
        <h3 className="text-sm font-medium text-ink-secondary mb-3">Tints (Adding White)</h3>
        {renderGrid(tints, false)}
      </div>

      <div>
        <h3 className="text-sm font-medium text-ink-secondary mb-3">Shades (Adding Black)</h3>
        {renderGrid(shades, true)}
      </div>
    </>
  );
};

export default TintsAndShades;
